<script setup lang="ts">
import { computed } from 'vue'
import Box from '@uiKit/elements/Box.vue'
import Text from '@uiKit/elements/Text.vue'
import Chip from '@uiKit/elements/Chip.vue'
import Skeleton from '@uiKit/elements/Skeleton.vue'
import IconBox from '@uiKit/components/IconBox.vue'
import type { PiIconName } from '@uiKit/elements/PiIcon.vue'
import type { ChipColor } from '@uiKit/elements/Chip.vue'
import { toneSurfaceOf } from '../../utils/toneSurface'
import type { PortfolioStatus } from '../../types/propertyTypes'

// The "Portfolio Status" tiles above the ledger.
//
// Two tiles are funding state (from the seller's offerings) and one is lifecycle
// state. The design's third tile was "Action Required" — dropped, because no
// property state is ever waiting on the seller (admin-service owns every
// transition), so the number would always have been zero or invented. "Under
// review" is the honest equivalent: it answers the same question ("is anything
// stuck?") with a real number.
const props = defineProps<{
  status: PortfolioStatus | null
  isLoading: boolean
}>()

interface Tile {
  key: string
  label: string
  value: number
  chipLabel: string | null
  chipColor: ChipColor
  caption: string
  icon: PiIconName
  tone: 'success' | 'primary' | 'warning'
}

const tiles = computed<Tile[]>(() => {
  const status = props.status
  if (!status) return []
  return [
    {
      key: 'fullyFunded',
      label: 'Fully Funded',
      value: status.fullyFundedCount,
      chipLabel: 'Completed',
      chipColor: 'success',
      caption: status.fullyFundedCount === 1 ? 'Property' : 'Properties',
      icon: 'verified',
      tone: 'success',
    },
    {
      key: 'liveFunding',
      label: 'Live Funding',
      value: status.liveFundingCount,
      chipLabel: 'Active',
      chipColor: 'success',
      caption: `Across ${status.totalPropertyCount} ${
        status.totalPropertyCount === 1 ? 'property' : 'properties'
      }`,
      icon: 'chart-bar',
      tone: 'primary',
    },
    {
      key: 'inReview',
      label: 'Under Review',
      value: status.inReviewPropertyCount,
      chipLabel: null,
      chipColor: 'warning',
      caption: 'Awaiting approval',
      icon: 'search',
      tone: 'warning',
    },
  ]
})

const SKELETON_TILES = 3
</script>

<template>
  <Box direction="col" gap="4" class="tiles">
    <Text is="h2" type="page-title" weight="bold">Portfolio Status</Text>

    <Box v-if="isLoading" flex gap="3" class="tiles__grid">
      <Skeleton v-for="index in SKELETON_TILES" :key="index" height="7.3rem" class="tiles__cell" />
    </Box>

    <Box v-else-if="tiles.length" flex gap="3" class="tiles__grid">
      <Box
        v-for="tile in tiles"
        :key="tile.key"
        direction="col"
        gap="3"
        class="tiles__cell"
        :class="`tiles__cell--${tile.tone}`"
      >
        <Box flex align="start" justify="between" gap="2">
          <Box direction="col" gap="2">
            <Text type="caption" color="muted" weight="medium">{{ tile.label }}</Text>
            <Box flex align="center" gap="2">
              <Text is="span" type="title-lg" weight="bold">{{ tile.value }}</Text>
              <Chip
                v-if="tile.chipLabel"
                :label="tile.chipLabel"
                :color="tile.chipColor"
                variant="outline"
                size="small"
              />
            </Box>
          </Box>
          <IconBox
            :icon="tile.icon"
            size="xl"
            :bg="toneSurfaceOf(tile.tone).bg"
            :icon-color="toneSurfaceOf(tile.tone).fg"
          />
        </Box>
        <Text type="caption-sm" color="muted">{{ tile.caption }}</Text>
      </Box>
    </Box>
  </Box>
</template>

<style scoped>
.tiles__grid {
  flex-wrap: wrap;
}

.tiles__cell {
  flex: 1 1 12rem;
  min-width: 0;
  padding: 1rem 1.25rem;
  border: 1px solid var(--p-content-border-color);
  border-radius: var(--p-border-radius-lg);
  background: var(--p-surface-0);
}

.tiles__cell--warning {
  border-color: var(--p-warning-300);
  background: var(--p-warning-50);
}
</style>
